import { Option } from "effect";
import { AsyncResult } from "effect/unstable/reactivity";
import { useMemo } from "react";
import { useCourseProgressBatch } from "#app/review-courses";

type CourseProgress = {
  courseId: string;
  totalLessons: number;
  completedLessons: number;
  completionPercentage: number;
  isFullyCompleted: boolean;
};

export function useCoursesProgress(courseIds: string[]): Map<string, CourseProgress> {
  const sortedIds = useMemo(() => [...courseIds].sort(), [courseIds]);

  const batchResult = useCourseProgressBatch(sortedIds);
  const batchData = AsyncResult.value(batchResult).pipe(Option.getOrUndefined);

  return useMemo(() => {
    const progressMap = new Map<string, CourseProgress>();

    if (!batchData) {
      // Return empty entries for each courseId so callers get consistent shape
      for (const courseId of courseIds) {
        progressMap.set(courseId, {
          courseId,
          totalLessons: 0,
          completedLessons: 0,
          completionPercentage: 0,
          isFullyCompleted: false,
        });
      }
      return progressMap;
    }

    // Group by courseId
    const grouped = new Map<string, Array<{ completed: boolean }>>();
    for (const row of batchData) {
      let arr = grouped.get(row.courseId);
      if (!arr) {
        arr = [];
        grouped.set(row.courseId, arr);
      }
      arr.push({ completed: row.completed });
    }

    for (const courseId of courseIds) {
      const lessons = grouped.get(courseId) ?? [];
      const totalLessons = lessons.length;
      const completedLessons = lessons.filter((p) => p.completed).length;

      const completionPercentage =
        totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

      progressMap.set(courseId, {
        courseId,
        totalLessons,
        completedLessons,
        completionPercentage,
        isFullyCompleted: completedLessons === totalLessons && totalLessons > 0,
      });
    }

    return progressMap;
  }, [courseIds, batchData]);
}
